import { useRef, useState } from "react";
import { Plus, Check, Lock, FileSpreadsheet, Upload, Download } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import { useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import stitchingOperationApi from "../api/stitchingOperationApi";
import { downloadBlob, blobErrorMessage } from "../utils/downloadBlob";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Modal from "../components/Modal";
import Alert from "../components/Alert";
import Badge from "../components/Badge";
import Table from "../components/Table";
import Select2 from "../components/Select2";
import SearchBar from "../components/SearchBar";
import T from "../theme/colors";

const EMPTY = { operationName: "", operationCode: "", description: "", isActive: true };

/**
 * Stitching Operation master — "Collar Attach", "Side Seam", "Button Hole".
 *
 * Only NAMES operations. The job work rate per piece and the line order depend
 * on the garment, so both are set in Barcode Operations, when an operation is
 * linked to a barcode.
 *
 * Excel: the export and the import share one layout, so an exported file can be
 * edited and uploaded straight back. The import is all-or-nothing.
 */
export default function StitchingOperationPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("STITCHING_OPERATION", "CREATE");
  const canEdit = hasPermission("STITCHING_OPERATION", "EDIT");
  const canDelete = hasPermission("STITCHING_OPERATION", "DELETE");
  const canExport = hasPermission("STITCHING_OPERATION", "EXPORT");

  const { mutate, loading: saving } = useMutation();

  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");   // "" | "active" | "inactive"

  const t = useServerTable(stitchingOperationApi.paged, {
    defaultSortBy: "operationName",
    defaultSortDir: "asc",
    defaultSize: 25,
    debounceMs: 300,
    extraFilters: {
      q: search.trim() || undefined,
      isActive: fStatus === "" ? undefined : fStatus === "active",
    },
  });

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");

  const f = key => val => setForm(p => ({ ...p, [key]: val }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setErrors({}); setModal(true); };
  const openEdit = row => {
    setEditing(row);
    setForm({
      operationName: row.operationName || "",
      operationCode: row.operationCode || "",
      description: row.description || "",
      isActive: row.isActive !== false,
    });
    setErrors({}); setModal(true);
  };
  const close = () => { setModal(false); setEditing(null); };

  const handleSave = async () => {
    const errs = {};
    if (!form.operationName.trim()) errs.operationName = "Operation name is required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const payload = {
      operationName: form.operationName.trim(),
      operationCode: form.operationCode.trim() || null,
      description: form.description.trim() || null,
      isActive: !!form.isActive,
    };
    await mutate(
      () => editing ? stitchingOperationApi.update(editing.id, payload) : stitchingOperationApi.create(payload),
      { onSuccess: () => { close(); t.reload(); }, successMsg: editing ? "Operation updated" : "Operation created" }
    );
  };

  const handleToggle = row => mutate(() => stitchingOperationApi.toggle(row.id), { onSuccess: t.reload });
  const handleDelete = row => {
    if (!window.confirm(`Delete "${row.operationName}"?\n\nIt is removed from barcodes' pick lists. Stitching entries `
      + `already saved with it keep their operation name and job work rate.`)) return;
    mutate(() => stitchingOperationApi.delete(row.id), { onSuccess: t.reload, successMsg: "Deleted" });
  };

  // The file matches the screen: same search and status filter.
  const exportExcel = async () => {
    setExporting(true); setExportErr("");
    try {
      const res = await stitchingOperationApi.exportExcel({
        q: search.trim() || undefined,
        isActive: fStatus === "" ? undefined : fStatus === "active",
      });
      downloadBlob(res, "stitching-operations.xlsx");
    } catch (e) {
      setExportErr(await blobErrorMessage(e, "Excel export failed"));
    }
    setExporting(false);
  };

  const cols = [
    {
      key: "operationName", label: "Operation", sortKey: "operationName",
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "var(--text-0)" }}>{v}</div>
          {row.description && (
            <div title={row.description} style={{
              fontSize: 12, color: "var(--text-2)", maxWidth: 360,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: "operationCode", label: "Code", sortKey: "operationCode",
      render: v => v
        ? <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-2)" }}>{v}</span>
        : <span style={{ color: "var(--text-2)" }}>—</span>,
    },
    {
      key: "isActive", label: "Status",
      render: v => <Badge label={v ? "ACTIVE" : "INACTIVE"} />,
    },
  ];

  return (
    <div className="fade-up" style={{ padding: "20px 24px" }}>
      <PageHeader
        title="Stitching Operations"
        subtitle="Create the stitching operations here. Their job work rate per piece and order are set per barcode in Barcode Operations."
        breadcrumb={["Masters", "Stitching Operations"]}
        actions={<>
          {canExport && <Btn variant="outline" size="sm" icon={<FileSpreadsheet size={13} />}
            loading={exporting} onClick={exportExcel}>Excel</Btn>}
          {canCreate && (
            <Btn variant="outline" size="sm" icon={<Upload size={13} />} onClick={() => setImportOpen(true)}>
              Import Excel
            </Btn>
          )}
          {canCreate
            ? <Btn size="sm" icon={<Plus size={13} />} onClick={openAdd}>Add Operation</Btn>
            : !(canEdit || canDelete) && <Btn variant="secondary" size="sm" icon={<Lock size={13} />} disabled>View Only</Btn>}
        </>}
      />

      <Alert message={exportErr} onClose={() => setExportErr("")} />

      <Card>
        <div style={{
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
        }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search operation or code…" />
          <div style={{ minWidth: 140 }}>
            <Select2 value={fStatus} onChange={setFStatus} placeholder="All statuses"
              options={[{ value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" }]} />
          </div>
        </div>

        <Table
          columns={cols}
          serverMode t={t}
          emptyText={search || fStatus ? "No operations match" : "No stitching operations yet — click Add Operation"}
          onEdit={canEdit ? openEdit : undefined}
          onToggle={canEdit ? handleToggle : undefined}
          onDelete={canDelete ? handleDelete : undefined}
        />
      </Card>

      <Modal
        open={modal} onClose={close} width={520}
        title={editing ? "Edit Operation" : "New Operation"}
        subtitle={editing ? editing.operationName : "Rate and order are set per barcode, in Barcode Operations"}
        footer={<>
          <Btn variant="secondary" onClick={close}>Cancel</Btn>
          <Btn onClick={handleSave} loading={saving} icon={<Check size={13} />}>
            {editing ? "Update Operation" : "Add Operation"}
          </Btn>
        </>}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Operation Name" required value={form.operationName} onChange={f("operationName")}
              placeholder="e.g. Collar Attach" error={errors.operationName} />
          </div>
          <Input label="Code" value={form.operationCode} onChange={f("operationCode")} placeholder="Optional, e.g. OP-COL" />
          <div />
          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Description" value={form.description} onChange={f("description")} placeholder="Optional" />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-1)" }}>
            <input type="checkbox" checked={!!form.isActive} onChange={e => f("isActive")(e.target.checked)} />
            Active
          </label>
        </div>
      </Modal>

      {importOpen && (
        <ImportModal onClose={() => setImportOpen(false)} onImported={t.reload} />
      )}
    </div>
  );
}

/**
 * Excel import: sample file, choose file, upload, then the outcome. Row errors
 * are listed with their Excel row number; with any error nothing was saved.
 */
function ImportModal({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const downloadSample = async () => {
    setErr("");
    try {
      downloadBlob(await stitchingOperationApi.importTemplate(), "stitching-operations-sample.xlsx");
    } catch (e) {
      setErr(await blobErrorMessage(e, "Could not download the sample file"));
    }
  };

  const upload = async () => {
    if (!file) { setErr("Choose an Excel file first"); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const res = await stitchingOperationApi.importExcel(file);
      const data = res.data?.data ?? res.data;
      setResult(data);
      if (!data?.errors?.length) onImported();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Import failed");
    }
    setBusy(false);
  };

  const failed = result?.errors?.length > 0;

  return (
    <Modal
      open onClose={onClose} width={600}
      title="Import Stitching Operations"
      subtitle="Adds new operations and updates existing ones, matched by operation name"
      footer={<>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        <Btn icon={<Upload size={13} />} loading={busy} disabled={!file} onClick={upload}>Import</Btn>
      </>}
    >
      <Alert message={err} onClose={() => setErr("")} />

      <div style={{
        border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px",
        background: "var(--bg-input)", fontSize: 13, color: "var(--text-1)", lineHeight: 1.6,
      }}>
        Columns: <b>Operation Name</b> (required), Code, Description, Status
        (Active / Inactive). An exported file can be edited and uploaded back. A blank cell leaves that
        field unchanged on an existing operation.
        <div style={{ marginTop: 10 }}>
          <Btn variant="outline" size="sm" icon={<Download size={13} />} onClick={downloadSample}>
            Download sample file
          </Btn>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
          onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setErr(""); }} />
        <Btn variant="secondary" size="sm" icon={<FileSpreadsheet size={13} />}
          onClick={() => fileRef.current?.click()}>Choose file</Btn>
        <span style={{ fontSize: 13, color: file ? "var(--text-0)" : "var(--text-2)" }}>
          {file ? file.name : "No file chosen"}
        </span>
      </div>

      {result && !failed && (
        <div style={{ marginTop: 14 }}>
          <Alert type="success" message={`Imported: ${result.created} added, ${result.updated} updated`
            + (result.skipped ? `, ${result.skipped} blank row${result.skipped === 1 ? "" : "s"} skipped` : "")
            + "."} />
        </div>
      )}

      {failed && (
        <div style={{ marginTop: 14 }}>
          <Alert type="warn" message={`Nothing was imported — ${result.errors.length} row`
            + `${result.errors.length === 1 ? " needs" : "s need"} fixing. Correct the file and import it again.`} />
          <div style={{ maxHeight: 240, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-input)" }}>
                  <th style={errTh}>Row</th>
                  <th style={errTh}>Problem</th>
                </tr>
              </thead>
              <tbody>
                {result.errors.map((er, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ ...errTd, width: 70, fontWeight: 700 }}>{er.row}</td>
                    <td style={{ ...errTd, color: "var(--danger)" }}>{er.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// CSS variables, so both themes read correctly.
const errTh = {
  textAlign: "left", padding: "8px 12px", fontSize: 12, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-2)",
};
const errTd = { padding: "8px 12px", color: "var(--text-0)", verticalAlign: "top" };
